import { Chat, Message } from "@/types/assistantChat"
import { UNPERSISTED_CHAT_ID } from "@/components/feature/Chat/utils/chatUtils"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import React, { createContext, useContext, useEffect, useState } from "react"

interface ChatContextType {
  chat: Chat
  setChat: React.Dispatch<React.SetStateAction<Chat>>
  streamingMessage: Message | null
  setStreamingMessage: React.Dispatch<React.SetStateAction<Message | null>>
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

const emptyChat: Chat = {
  id: UNPERSISTED_CHAT_ID,
  title: "",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  messages: [],
  webSearchEnabled: false,
}

const LOCAL_STORAGE_CHAT = "turnus-chat"

export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const chatHistoryEnabled = useFeatureFlag("CHAT_HISTORY")
  const shouldUseLocalStorage = !chatHistoryEnabled
  const storedChat = shouldUseLocalStorage
    ? localStorage.getItem(LOCAL_STORAGE_CHAT)
    : null
  const parsedChat = storedChat ? JSON.parse(storedChat) : null
  const initialChat: Chat = parsedChat
    ? {
        ...emptyChat,
        ...parsedChat,
        webSearchEnabled: parsedChat.webSearchEnabled ?? false,
      }
    : emptyChat

  const [chat, setChat] = useState<Chat>(initialChat)
  const [streamingMessage, setStreamingMessage] = useState<Message | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!shouldUseLocalStorage) {
      return
    }
    localStorage.setItem(LOCAL_STORAGE_CHAT, JSON.stringify(chat))
  }, [chat, shouldUseLocalStorage])

  return (
    <ChatContext.Provider
      value={{
        chat,
        setChat,
        streamingMessage,
        setStreamingMessage,
        isLoading,
        setIsLoading,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export const useChatContext = () => {
  const context = useContext(ChatContext)
  if (!context)
    throw new Error("useChatContext must be used within ChatProvider")
  return context
}
